import React, { useState, useRef, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";
import { GrFormAdd } from "react-icons/gr";
import { MdDeleteForever } from "react-icons/md";
import { BsCloudUpload } from "react-icons/bs";
import moment from "moment";

import ActionButton from "../../../../Common/ActionButton";
import { InputField, SelectField, TextAreaField } from "../../../../Common/FormComponents";
import { useSession } from "../../../../../context/SessionContext";
import { filterStoreProjects } from "../../../projectFilters";
import { formatAmountWithCommas } from "../../../../../Utils/utils";
import { getallVendors } from "../../../../../Redux/actions/Operationsactions/getvendors";
import { getallMaterial } from "../../../../../Redux/actions/Operationsactions/getmaterial";
import { saveQuote, getQuoteById, getQuotableRequisitions } from "./quotesService";
import "../../../AddContractorsAndVendors.css";

const headerCellStyle = {
  height: "42px",
  paddingTop: "6px",
  paddingBottom: "6px",
  verticalAlign: "middle",
  lineHeight: "1.1",
};

const paymentTermsOptions = [
  { value: "15 Days Credit", label: "15 Days Credit" },
  { value: "30 Days Credit", label: "30 Days Credit" },
  { value: "Credit - 7 Days", label: "Credit - 7 Days" },
  { value: "Advance / COD", label: "Advance / COD" },
  { value: "100% Advance", label: "100% Advance" },
  { value: "Running Bill / Progress Payment", label: "Running Bill / Progress Payment" },
];

const emptyLineItem = () => ({
  id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
  MaterialId: "",
  MaterialName: "",
  Specification: "",
  Uom: "",
  Qty: 1,
  UnitRate: 0,
  TotalAmount: 0,
  Remarks: "",
});

const AddQuote = () => {
  const history = useHistory();
  const location = useLocation();
  const { rolesdata } = useSession();

  const fileInputRef = useRef(null);

  // Projects list
  const projectList = useMemo(() => {
    return filterStoreProjects(rolesdata?.data?.Projects) || [];
  }, [rolesdata]);

  const populateData = location.state || {};
  const isEdit = Boolean(populateData?.isEdit);

  const dispatch = useDispatch();
  const vendorsData = useSelector((state) => state.getAllVendors?.data || []);
  const materialRedux = useSelector((state) => state.getAllMaterial?.data || []);

  const [isLoading, setIsLoading] = useState(false);
  const [requisitions, setRequisitions] = useState([]);
  const [attachedFile, setAttachedFile] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    QuoteNo: "",
    VendorId: "",
    VendorName: "",
    ProjectId: "",
    RequisitionId: "",
    RequisitionNo: "",
    QuoteDate: moment().format("YYYY-MM-DD"),
    ValidTill: moment().add(15, "days").format("YYYY-MM-DD"),
    PaymentTerms: "15 Days Credit",
    DeliveryLeadTime: "",
    FreightAmount: 0,
    Status: "Received",
    AttachmentName: "",
    Remarks: "",
  });

  // Items State
  const [items, setItems] = useState([emptyLineItem()]);

  // A quote without a requisition is a direct quote: lines are picked from the Material master list.
  const isDirect = !formData.RequisitionId;

  useEffect(() => {
    dispatch(getallMaterial());
  }, [dispatch]);

  // Vendors and quotable requisitions follow the project.
  useEffect(() => {
    if (!formData.ProjectId) {
      setRequisitions([]);
      return undefined;
    }
    let cancelled = false;
    dispatch(getallVendors(formData.ProjectId));
    getQuotableRequisitions(formData.ProjectId)
      .then((list) => {
        if (!cancelled) {
          setRequisitions(list);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err.message || "Failed to load requisitions");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, formData.ProjectId]);

  // Edit: the list row carries no lines, so load the quote itself.
  useEffect(() => {
    if (!isEdit || !populateData.Id) {
      return;
    }
    getQuoteById(populateData.Id)
      .then((q) => {
        if (q.InComparison) {
          toast.error("This quote is part of a comparison and can no longer be edited.");
          history.push("/procurement/quotations/quotes");
          return;
        }
        setFormData({
          QuoteNo: q.QuoteNo,
          VendorId: String(q.VendorId),
          VendorName: q.VendorName,
          ProjectId: String(q.ProjectId),
          RequisitionId: q.RequisitionId ? String(q.RequisitionId) : "",
          RequisitionNo: q.RequisitionNo,
          QuoteDate: q.QuoteDate,
          ValidTill: q.ValidTill,
          PaymentTerms: q.PaymentTerms,
          DeliveryLeadTime: q.DeliveryLeadTime,
          FreightAmount: q.FreightAmount,
          Status: q.Status,
          AttachmentName: q.AttachmentName,
          Remarks: q.Remarks,
        });
        setItems(q.Items.length > 0 ? q.Items : [emptyLineItem()]);
      })
      .catch((err) => {
        toast.error(err.message || "Failed to load quotation");
        history.push("/procurement/quotations/quotes");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const vendorOptions = useMemo(() => {
    const list = vendorsData.map((v) => ({ value: String(v.Id), label: v.Name }));
    if (formData.VendorId && !list.some((o) => o.value === formData.VendorId)) {
      list.push({ value: formData.VendorId, label: formData.VendorName });
    }
    return list;
  }, [vendorsData, formData.VendorId, formData.VendorName]);

  const requisitionOptions = useMemo(() => {
    const list = requisitions.map((r) => ({ value: r.value, label: r.label }));
    if (formData.RequisitionId && !list.some((o) => o.value === formData.RequisitionId)) {
      list.push({ value: formData.RequisitionId, label: formData.RequisitionNo });
    }
    return list;
  }, [requisitions, formData.RequisitionId, formData.RequisitionNo]);

  const termsOptions = useMemo(
    () =>
      paymentTermsOptions.some((o) => o.value === formData.PaymentTerms) || !formData.PaymentTerms
        ? paymentTermsOptions
        : [...paymentTermsOptions, { value: formData.PaymentTerms, label: formData.PaymentTerms }],
    [formData.PaymentTerms],
  );

  // Calculate totals
  const itemsTotal = useMemo(() => {
    return items.reduce((acc, it) => acc + (Number(it.TotalAmount) || 0), 0);
  }, [items]);

  const grandTotal = useMemo(() => {
    return itemsTotal + (Number(formData.FreightAmount) || 0);
  }, [itemsTotal, formData.FreightAmount]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    // A new project invalidates the vendor, the requisition and the lines.
    if (name === "ProjectId") {
      setFormData((prev) => ({
        ...prev,
        ProjectId: value,
        VendorId: "",
        RequisitionId: "",
        RequisitionNo: "",
      }));
      setItems([emptyLineItem()]);
      return;
    }

    // Requisition changed: auto-fill the items from it ("None / Direct Quote" = free lines).
    if (name === "RequisitionId") {
      const match = requisitions.find((r) => r.value === value);
      setFormData((prev) => ({
        ...prev,
        RequisitionId: value,
        RequisitionNo: match?.requisitionNo || "",
      }));
      if (match) {
        setItems(match.items.map((mi) => ({ ...mi, UnitRate: 0, TotalAmount: 0, Remarks: "" })));
        toast.info(`Materials automatically loaded from requisition ${match.requisitionNo}`);
      } else {
        setItems([emptyLineItem()]);
      }
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Direct quote: picking a material fills its base unit.
  const handleMaterialPick = (id, materialId) => {
    const m = materialRedux.find((x) => x.Id === materialId);
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              MaterialId: materialId,
              MaterialName: m?.ItemName || "",
              Uom: m?.UomName || "",
            }
          : item,
      ),
    );
  };

  const handleItemChange = (id, field, value) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          if (field === "Qty" || field === "UnitRate") {
            const q = field === "Qty" ? Number(value) || 0 : Number(item.Qty) || 0;
            const r = field === "UnitRate" ? Number(value) || 0 : Number(item.UnitRate) || 0;
            updated.TotalAmount = q * r;
          }
          return updated;
        }
        return item;
      }),
    );
  };

  const handleAddItem = () => {
    if (isDirect) {
      setItems((prev) => [...prev, emptyLineItem()]);
      return;
    }
    // Requisition quote: re-add a requisition line that was removed.
    const req = requisitions.find((r) => r.value === formData.RequisitionId);
    const missing = req?.items.find((mi) => !items.some((i) => i.id === mi.id));
    if (!missing) {
      toast.info("All requisition lines are already in the quotation");
      return;
    }
    setItems((prev) => [...prev, { ...missing, UnitRate: 0, TotalAmount: 0, Remarks: "" }]);
  };

  const handleRemoveItem = (id) => {
    if (items.length <= 1) {
      toast.warning("Quotation must have at least one line item");
      return;
    }
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAttachedFile(file);
      setFormData((prev) => ({
        ...prev,
        AttachmentName: file.name,
      }));
      toast.success(`Attached "${file.name}"`);
    }
  };

  const handleSave = async () => {
    if (!formData.VendorId) {
      toast.error("Please select a Vendor");
      return;
    }
    if (!formData.ProjectId) {
      toast.error("Please select a Project / Site");
      return;
    }
    if (!formData.QuoteDate) {
      toast.error("Please specify Quote Date");
      return;
    }
    if (formData.ValidTill && formData.ValidTill < formData.QuoteDate) {
      toast.error("Valid till cannot be before the quote date");
      return;
    }
    if (items.length === 0) {
      toast.error("Please add at least one line item");
      return;
    }
    const noOpen = !isDirect && items.find((i) => Number(i.Qty) <= 0);
    if (noOpen) {
      toast.error(`No open quantity left on ${noOpen.MaterialName}. Remove this line to save.`);
      return;
    }
    const hasEmpty = items.some(
      (i) => (isDirect ? !i.MaterialId : !i.MaterialName.trim()) || Number(i.Qty) <= 0,
    );
    if (hasEmpty) {
      toast.error("Please enter material name and quantity for all items");
      return;
    }
    const negative = items.find((i) => Number(i.UnitRate) < 0);
    if (negative) {
      toast.error(`Unit rate for ${negative.MaterialName || "an item"} cannot be negative`);
      return;
    }
    if (!items.some((i) => Number(i.UnitRate) > 0)) {
      toast.error("Please enter a unit rate for at least one item");
      return;
    }

    setIsLoading(true);

    try {
      const saved = await saveQuote({
        Id: isEdit ? populateData.Id : "",
        VendorId: formData.VendorId,
        ProjectId: formData.ProjectId,
        RequisitionId: formData.RequisitionId,
        QuoteDate: formData.QuoteDate,
        ValidTill: formData.ValidTill,
        PaymentTerms: formData.PaymentTerms,
        DeliveryLeadTime: formData.DeliveryLeadTime,
        FreightAmount: Number(formData.FreightAmount) || 0,
        Remarks: formData.Remarks,
        File: attachedFile,
        RemoveFile: isEdit && !formData.AttachmentName,
        Items: items,
      });
      const no = saved.QuoteNumber || formData.QuoteNo;
      toast.success(
        isEdit ? `Quotation ${no} updated successfully!` : `Quotation ${no} created successfully!`,
      );
      history.push("/procurement/quotations/quotes");
    } catch (err) {
      toast.error(err.message || "Failed to save quotation");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {/* ── Page Header ── */}
      <div className="row">
        <div className="col-md-12 text-left page_heading">
          <div className="d-flex align-items-center">
            <FaArrowLeft
              size={16}
              style={{ cursor: "pointer", marginRight: "12px" }}
              title="Back"
              onClick={() => history.goBack()}
            />
            <h4 className="m-0">{isEdit ? "Edit" : "Add"} Quotation</h4>
          </div>
        </div>
      </div>

      {/* ── Header Fields ── */}
      <div className="p-0 mt-3">
        <h6 style={{ color: "#056363", fontWeight: 600, marginBottom: "16px" }}>
          Quotation Reference Details
        </h6>

        <div className="row mb-2">
          <InputField
            label="Quote No:"
            name="QuoteNo"
            value={formData.QuoteNo || "Assigned on save"}
            disabled={true}
            stacked
            wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
          />

          <SelectField
            label="Project / Site:"
            name="ProjectId"
            value={formData.ProjectId}
            onChange={handleFormChange}
            options={[
              { value: "", label: "Select Site" },
              ...projectList.map((p) => ({
                value: String(p.ProjectId),
                label: p.ProjectName,
              })),
            ]}
            disabled={isEdit}
            required
            stacked
            wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
          />
          <SelectField
            label="Vendor / Supplier:"
            name="VendorId"
            value={formData.VendorId}
            onChange={handleFormChange}
            options={[{ value: "", label: "Select Vendor" }, ...vendorOptions]}
            required
            stacked
            wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
          />

          <SelectField
            label="Linked Requisition:"
            name="RequisitionId"
            value={formData.RequisitionId}
            onChange={handleFormChange}
            options={[{ value: "", label: "None / Direct Quote" }, ...requisitionOptions]}
            disabled={isEdit}
            stacked
            wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
          />
          <InputField
            label="Quote Date:"
            name="QuoteDate"
            type="date"
            value={formData.QuoteDate}
            onChange={handleFormChange}
            required
            stacked
            wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
          />

          <InputField
            label="Valid Till:"
            name="ValidTill"
            type="date"
            value={formData.ValidTill}
            onChange={handleFormChange}
            stacked
            wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
          />
          <SelectField
            label="Payment & Credit Terms:"
            name="PaymentTerms"
            value={formData.PaymentTerms}
            onChange={handleFormChange}
            options={termsOptions}
            required
            stacked
            wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
          />

          <InputField
            label="Delivery Lead Time:"
            name="DeliveryLeadTime"
            value={formData.DeliveryLeadTime}
            placeholder="e.g. 2-3 Days, Immediate"
            onChange={handleFormChange}
            stacked
            wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
          />
          <InputField
            label="Carriage / Freight (PKR):"
            name="FreightAmount"
            type="number"
            value={formData.FreightAmount}
            onChange={handleFormChange}
            placeholder="0"
            stacked
            wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
          />

          <SelectField
            label="Status:"
            name="Status"
            value={formData.Status}
            onChange={handleFormChange}
            disabled={true}
            options={[
              { value: "Received", label: "Received" },
              { value: "Awarded", label: "Awarded" },
              { value: "Partially Awarded", label: "Partially Awarded" },
              { value: "Not Awarded", label: "Not Awarded" },
              { value: "Expired", label: "Expired" },
            ]}
            stacked
            wrapperCol="col-xl-3 col-lg-4 col-md-6 col-12"
          />
        </div>

        {/* ── Quoted Items Table ── */}
        <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
          <h6 className="m-0" style={{ color: "#056363", fontWeight: 600 }}>
            Quoted Materials & Unit Rates
          </h6>
          <button
            type="button"
            className="btn btn-sm btn-outline-success d-flex align-items-center"
            style={{ fontSize: "12px", gap: "4px" }}
            onClick={handleAddItem}
          >
            <GrFormAdd size="16" />
            <span>Add Line Item</span>
          </button>
        </div>

        <div className="row mt-2 clearfix">
          <div className="col-md-12 p-0 column">
            <div className="table-responsive">
              <table className="table table-bordered table-hover" style={{ fontSize: "13px" }}>
                <thead style={{ backgroundColor: "#E8FBFB" }}>
                  <tr>
                    <th style={{ ...headerCellStyle, width: "40px", textAlign: "center" }}>#</th>
                    <th style={{ ...headerCellStyle, minWidth: "180px" }}>
                      Material / Description *
                    </th>
                    <th style={{ ...headerCellStyle, minWidth: "160px" }}>Specification / Grade</th>
                    <th style={{ ...headerCellStyle, width: "100px", textAlign: "right" }}>
                      Qty *
                    </th>
                    <th style={{ ...headerCellStyle, width: "100px" }}>UOM</th>
                    <th style={{ ...headerCellStyle, width: "140px", textAlign: "right" }}>
                      Unit Rate *
                    </th>
                    <th style={{ ...headerCellStyle, width: "150px", textAlign: "right" }}>
                      Total Amount
                    </th>
                    <th style={{ ...headerCellStyle, minWidth: "160px" }}>Remarks / Delivery</th>
                    <th style={{ ...headerCellStyle, width: "60px", textAlign: "center" }}>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={item.id}>
                      <td style={{ verticalAlign: "middle", textAlign: "center" }}>{index + 1}</td>
                      <td>
                        {isDirect ? (
                          <select
                            className="input_styling w-100"
                            value={item.MaterialId || ""}
                            onChange={(e) => handleMaterialPick(item.id, e.target.value)}
                          >
                            <option value="">Select Material</option>
                            {materialRedux
                              .filter(
                                (m) =>
                                  m.Id === item.MaterialId ||
                                  !items.some((o) => o.MaterialId === m.Id),
                              )
                              .map((m) => (
                                <option key={m.Id} value={m.Id}>
                                  {m.ItemName}
                                </option>
                              ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            className="input_styling w-100"
                            value={item.MaterialName}
                            readOnly
                          />
                        )}
                      </td>
                      <td>
                        <input
                          type="text"
                          className="input_styling w-100"
                          value={item.Specification}
                          placeholder="Grade 53 / ASTM A615"
                          onChange={(e) =>
                            handleItemChange(item.id, "Specification", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="input_styling w-100 text-right"
                          value={item.Qty}
                          min="1"
                          readOnly={!isDirect}
                          onChange={(e) => handleItemChange(item.id, "Qty", e.target.value)}
                        />
                      </td>
                      <td>
                        <select className="input_styling w-100" value={item.Uom} disabled>
                          <option value={item.Uom}>{item.Uom}</option>
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          className="input_styling w-100 text-right"
                          value={item.UnitRate}
                          placeholder="0"
                          onChange={(e) => handleItemChange(item.id, "UnitRate", e.target.value)}
                        />
                      </td>
                      <td
                        style={{
                          verticalAlign: "middle",
                          textAlign: "right",
                          fontWeight: 700,
                          color: "#056363",
                        }}
                      >
                        {formatAmountWithCommas(item.TotalAmount || 0)}
                      </td>
                      <td>
                        <input
                          type="text"
                          className="input_styling w-100"
                          value={item.Remarks}
                          placeholder="e.g. Ex-factory rate"
                          onChange={(e) => handleItemChange(item.id, "Remarks", e.target.value)}
                        />
                      </td>
                      <td style={{ verticalAlign: "middle", textAlign: "center" }}>
                        <MdDeleteForever
                          size="1.3em"
                          style={{ cursor: "pointer", color: "#c0392b" }}
                          title="Remove Line"
                          onClick={() => handleRemoveItem(item.id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── Vendor Notes & Attachment ── */}
        <div className="row mt-3 mb-3 align-items-start">
          <TextAreaField
            label="Notes :"
            name="Remarks"
            value={formData.Remarks}
            placeholder="Enter payment, delivery, warranty, or validity notes..."
            onChange={handleFormChange}
            stacked
            wrapperCol="col-lg-4 col-md-6 col-12"
            rows={4}
          />
          <div className="col-lg-4 col-md-6 col-12 stacked-form-field mb-3">
            <label className="input_label d-block m-0 mb-2">Attachment (Quote PDF)</label>
            <div
              className="document-upload-box"
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
            >
              <BsCloudUpload className="document-upload-icon" />
              {formData.AttachmentName ? (
                <div className="document-upload-file">
                  <span title={formData.AttachmentName}>{formData.AttachmentName}</span>
                  <button
                    type="button"
                    className="document-upload-remove"
                    onClick={(event) => {
                      event.stopPropagation();
                      setAttachedFile(null);
                      setFormData((previous) => ({ ...previous, AttachmentName: "" }));
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                    }}
                    title="Remove attachment"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className="document-upload-copy">
                  <strong>Click to upload</strong>
                  <span>PDF, image or document</span>
                </div>
              )}
              <input
                type="file"
                ref={fileInputRef}
                className="d-none"
                onChange={handleFileUpload}
                accept=".pdf,.doc,.docx,.jpg,.png"
              />
            </div>
          </div>
          <div className="col-lg-4 col-md-6 col-12">
            <div
              style={{
                backgroundColor: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                padding: "16px 20px",
                fontSize: "13px",
              }}
            >
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Items Subtotal:</span>
                <span style={{ fontWeight: 600 }}>PKR {formatAmountWithCommas(itemsTotal)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Carriage / Freight:</span>
                <span style={{ fontWeight: 600 }}>
                  PKR {formatAmountWithCommas(formData.FreightAmount || 0)}
                </span>
              </div>
              <div
                className="d-flex justify-content-between pt-2"
                style={{
                  borderTop: "2px solid #056363",
                  fontWeight: 700,
                  fontSize: "15px",
                  color: "#056363",
                }}
              >
                <span>Grand Total:</span>
                <span>PKR {formatAmountWithCommas(grandTotal)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Bottom Submit Actions ── */}
        <div className="float-right mt-2 mr-md-3 mb-5" style={{ display: "flex", gap: "10px" }}>
          <ActionButton
            text="Cancel"
            variant="cancel"
            onClick={() => history.push("/procurement/quotations/quotes")}
          />
          <ActionButton
            text={isLoading ? "Saving..." : isEdit ? "Update" : "Save"}
            onClick={handleSave}
            disabled={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default AddQuote;
